import { Flex, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import Loading from 'src/pages/Status/Loading';
import { fetchActiveRevision, fetchSubmissionFeedback } from 'src/api/queries';
import { ReviewHistoryForAuthor } from './components/ReviewHistory/ReviewHistoryForAuthor';
import ChangeRequests from './components/ChangeRequest';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { IMySubmissionTab } from 'src/constants';
import { UnsubmitFeedback } from './components/feedback/UnsubmitFeedback';
import { DecisionFeedback } from './components/feedback/DecisionFeedback';
import { RevisionFeedback } from './components/feedback/RevisionFeedback';
import { TransferOfferFeedback } from './components/feedback/TransferOfferFeedback';
import { RejectFeedback } from './components/feedback/RejectedFeedback';
import AuthorSubmittedResponse from './components/AuthorResponse/AuthorSubmittedResponse';
import { PreProductionFeedback } from './components/feedback/PreProductionFeedback';

export type FeedbackTabProps = {
  setActiveTab: (tab: IMySubmissionTab) => void;
  setIsFormDirty: (value: boolean) => void;
  handleNavigate: () => void;
  isEditingAllowed: boolean;
};
export const FeedbackTab: React.FC<FeedbackTabProps> = ({ setActiveTab }) => {
  const {
    workflowResponse: {
      submissionId,
      workflowData: { currentState, currentDeadline, dateEnteredCurrentPhase }
    },
    transferOffer: transferOfferResponse,
    changeRequestData,
    unsubmit,
    rejection,
    fieldsResponse: { fields },
    availableActions
  } = useSubmissionData();

  const { checkFormFieldValueIsUpdated } = useSubmissionFields('submission');

  const { data: feedbackResponse, isLoading: isLoadingFeedback } = useQuery({
    queryKey: ['feedback', { submissionId }],
    queryFn: () => fetchSubmissionFeedback(submissionId),
    retry: 0
  });

  const { data, isLoading: isLoading } = useQuery({
    queryKey: ['active-revision', { submissionId, roleType: 'author' }],
    queryFn: () => fetchActiveRevision(submissionId, 'author'),
    retry: 0
  });

  if (isLoading || isLoadingFeedback) return <Loading />;

  const isEmpty = !(
    data ||
    feedbackResponse?.feedback.length ||
    transferOfferResponse?.targetJournals?.length ||
    (currentState==="unsubmitted" && unsubmit) ||
    rejection ||
    changeRequestData?.changeRequest.requestHistory?.length
  );

  const EmptyChangeRequest = () => {
    return (
      <Flex flexDirection="column" gap={8} paddingBottom={8}>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingTitle">No Requests Available</Text>
          <Text textStyle="textBody" >
            There are no updates required for your submission as of now. You
            will be notified if your submission requires any updates or
            revisions.
          </Text>
        </Flex>
      </Flex>
    );
  };

  if (isEmpty) return <EmptyChangeRequest />;

  const showChangeRequest =
    availableActions.contains('AuthorCompleteChangeRequest#fields') ||
    availableActions.contains('AuthorCompleteChangeRequest#form');

  return (
    <Flex gap={8} flexDirection="column">
      {currentState === 'rejected' && (
        <RejectFeedback
          data={rejection}
          dateEntered={dateEnteredCurrentPhase}
          submissionContextId={submissionId}
        />
      )}
      {currentState === 'unsubmitted' && (
        <UnsubmitFeedback
          data={unsubmit}
          dateEntered={dateEnteredCurrentPhase}
          submissionContextId={submissionId}
        />
      )}
      {currentState === 'preProduction' && (
        <PreProductionFeedback submissionId={submissionId} />
      )}

      {data && !data?.editorialDecision ? (
        <RevisionFeedback
          revisionRound={data}
          currentDeadline={currentDeadline}
          submissionId={submissionId}
        />
      ) : null}

      {[
        'revisionAssessment',
        'revisionPeerReviewInvitation',
        'revisionPeerReviewPending',
        'revisionPeerReviewFinished'
      ].includes(currentState) &&
        data &&
        !data?.editorialDecision && (
          <AuthorSubmittedResponse revisionRound={data} />
        )}

      {showChangeRequest && (
        <ChangeRequests
          submissionFields={fields}
          changeRequest={changeRequestData?.changeRequest}
          checkFormFieldValueIsUpdated={checkFormFieldValueIsUpdated}
          setActiveTab={setActiveTab}
          submissionId={submissionId}
        />
      )}

      {feedbackResponse?.feedback.map(feedback => {
        switch (feedback.type) {
          case 'decision':
            return (
              <DecisionFeedback key={feedback.id} payload={feedback.payload} />
            );
          default:
            return null;
        }
      })}

      {!!transferOfferResponse?.targetJournals?.length && (
        <TransferOfferFeedback
          payload={transferOfferResponse}
          currentDeadline={currentDeadline}
          submissionContextId={submissionId}
        />
      )}

      <ReviewHistoryForAuthor />
    </Flex>
  );
};
